import {
  AnimatedButtonContainer,
  AnimatedButtonRow,
  Button,
} from "@babackapp/customer-ui/components/button";
import {
  Header,
  HeaderDescription,
  HeaderTitle,
} from "@babackapp/customer-ui/components/header";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@babackapp/customer-ui/components/select";
import { WidgetPage } from "@babackapp/customer-ui/components/widget-page";
import { useCatalogProduct } from "@babackapp/private-api-client/client/use-catalog-product";
import { useCustomText } from "@babackapp/return-widget-components/hooks/use-custom-text";
import { useDraftReturn } from "@babackapp/return-widget-components/hooks/use-draft-return";
import { useSetRebuyItems } from "@babackapp/return-widget-components/hooks/use-set-rebuy-items";
import { useUpdateLineItem } from "@babackapp/return-widget-components/hooks/use-update-line-item";
import { EnterIcon } from "@radix-ui/react-icons";
import { ArrowLeft } from "lucide-react";
import { motion } from "motion/react";
import { useState } from "react";
import {
  Link,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useProductVariantPicker } from "../../hooks/use-product-variant-picker";
import { useWidgetParams } from "../../hooks/use-widget-params";
import { buildCartItem } from "../../lib/build-cart-item";

type LocationState = { imageUrl?: string | null; label?: string } | null;

export function ProductSelection() {
  const { lineItemId = "" } = useParams<{ lineItemId: string }>();
  const [searchParams] = useSearchParams();
  const productId = searchParams.get("productId") ?? "";
  const state = useLocation().state as LocationState;
  const { orderId, shopInternalId } = useWidgetParams();
  const updateLineItem = useUpdateLineItem(orderId);
  const setRebuyItems = useSetRebuyItems(orderId);
  const draft = useDraftReturn(orderId);
  const navigate = useNavigate();
  const t = useCustomText();
  const [isConfirming, setIsConfirming] = useState(false);

  const { data: product } = useCatalogProduct(shopInternalId, productId);
  const options = product?.options ?? [];
  const variants = product?.variants ?? [];
  const { selectedOptions, selectedVariant, selectableOptions, handleOptionChange } =
    useProductVariantPicker(options, variants);

  const imageUrl =
    selectedVariant?.imageUrl ?? product?.featuredImageUrl ?? state?.imageUrl ?? null;

  async function handleConfirm() {
    if (!selectedVariant || isConfirming) return;
    setIsConfirming(true);

    const cartItem = await buildCartItem(
      shopInternalId,
      product?.title ?? "",
      selectedVariant,
      imageUrl,
    );

    updateLineItem(lineItemId, { resolution: "exchange-credit", exchangeVariantId: null });
    setRebuyItems([...(draft?.rebuyItems ?? []), cartItem]);
    navigate("../../../shop-now-resolve");
  }

  return (
    <WidgetPage>
      <Header>
        <HeaderTitle>{product?.title ?? state?.label ?? t("productSelection.title")}</HeaderTitle>
        <HeaderDescription>{t("productSelection.description")}</HeaderDescription>
      </Header>

      <div className="flex justify-center py-4">
        <div className="size-40 overflow-hidden rounded-lg bg-muted">
          {imageUrl && (
            <motion.img
              layoutId={`suggestion-image-${productId}`}
              src={imageUrl}
              alt={product?.title ?? ""}
              className="size-full object-cover"
            />
          )}
        </div>
      </div>

      <div className="flex flex-wrap justify-center gap-2">
        {selectableOptions.map((opt) => (
          <Select
            key={opt.name}
            value={selectedOptions[opt.name] ?? ""}
            onValueChange={(v) => handleOptionChange(opt.name, v)}
          >
            <SelectTrigger className="w-auto" aria-label={opt.name}>
              <SelectValue placeholder={opt.name} />
            </SelectTrigger>
            <SelectContent>
              {opt.values.map((v) => (
                <SelectItem key={v} value={v}>
                  {v}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ))}
      </div>

      <AnimatedButtonRow variants={{}}>
        <AnimatedButtonContainer
          layoutId="line-item-back-button"
          disableVariants={{ exit: true }}
        >
          <Link to="../resolution">
            <Button variant="secondary" type="button">
              <ArrowLeft />
              {t("common.back")}
            </Button>
          </Link>
        </AnimatedButtonContainer>
        <AnimatedButtonContainer
          layoutId="line-item-confirm-button"
          disableVariants={{ enter: true, exit: true }}
        >
          <Button
            type="button"
            disabled={!selectedVariant || isConfirming}
            onClick={handleConfirm}
          >
            <EnterIcon />
            {t("common.confirm")}
          </Button>
        </AnimatedButtonContainer>
      </AnimatedButtonRow>
    </WidgetPage>
  );
}
